/**
 * Export and file generation utility functions for FleetSkipper
 */

/**
 * Create and download a blob as a file
 * @param {Blob} blob - Blob to download
 * @param {string} filename - Filename for download
 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Create HTML blob from content
 * @param {string} htmlContent - HTML content string
 * @returns {Blob} HTML blob
 */
export function createHTMLBlob(htmlContent) {
  return new Blob([htmlContent], { type: 'text/html' });
}

/**
 * Create JSON blob from data
 * @param {Object} data - Data to convert to JSON
 * @returns {Blob} JSON blob
 */
export function createJSONBlob(data) {
  const jsonString = JSON.stringify(data, null, 2);
  return new Blob([jsonString], { type: 'application/json' });
}

/**
 * Create CSV blob from data
 * @param {string} csvContent - CSV content string
 * @returns {Blob} CSV blob
 */
export function createCSVBlob(csvContent) {
  return new Blob([csvContent], { type: 'text/csv' });
}

/**
 * Sanitize filename for safe file system usage
 * @param {string} filename - Original filename
 * @returns {string} Sanitized filename
 */
export function sanitizeFilename(filename) {
  return filename.replace(/[^a-z0-9]/gi, '_');
}

/**
 * Generate date-stamped filename
 * @param {string} baseName - Base filename
 * @param {string} extension - File extension (with or without dot)
 * @param {Date} date - Date to use for timestamp (defaults to now)
 * @returns {string} Timestamped filename
 */
export function generateTimestampedFilename(baseName, extension, date = new Date()) {
  const timestamp = date.toISOString().split('T')[0];
  const sanitizedBase = sanitizeFilename(baseName);
  const ext = extension.startsWith('.') ? extension : `.${extension}`;
  return `${sanitizedBase}_${timestamp}${ext}`;
}

/**
 * Format vessel details for reports
 * @param {Object} settings - Vessel settings
 * @returns {string} Formatted vessel details string
 */
export function formatVesselDetails(settings) {
  const details = [
    settings.certifyingAuthority && `Authority: ${settings.certifyingAuthority}`,
    settings.officialNumber && `Official No: ${settings.officialNumber}`,
    settings.mmsiCallSign && `MMSI/Call: ${settings.mmsiCallSign}`
  ].filter(Boolean);
  
  return details.join(" | ");
}

/**
 * Generate report header HTML
 * @param {string} title - Report title
 * @param {string} vesselName - Vessel name
 * @param {Object} branding - Branding options
 * @param {string} dateRange - Date range string
 * @returns {string} HTML header content
 */
export function generateReportHeader(title, vesselName, branding = {}, dateRange = '') {
  return `
    <div class="header">
      ${branding.companyName ? `<div class="company-name">${branding.companyName}</div>` : ''}
      <h1 class="vessel-name">🚢 ${vesselName}</h1>
      <h2>${title}</h2>
      ${dateRange ? `<div class="date-range">${dateRange}</div>` : ''}
      ${branding.headerNotes ? `<div style="margin-top: 15px; font-style: italic;">${branding.headerNotes}</div>` : ''}
    </div>
  `;
}

/**
 * Generate report footer HTML
 * @param {Object} branding - Branding options
 * @returns {string} HTML footer content
 */
export function generateReportFooter(branding = {}) {
  return `
    <div class="footer">
      <p><strong>Report generated:</strong> ${new Date().toLocaleString()}</p>
      <p><strong>Generated by:</strong> FleetSkipper v2.0</p>
      ${branding.footerNotes ? `<p><strong>Additional Notes:</strong> ${branding.footerNotes}</p>` : ''}
    </div>
  `;
}

/**
 * Generate standard CSS styles for reports
 * @returns {string} CSS styles
 */
export function generateReportStyles() {
  return `
    <style>
      body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
      .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #059669; padding-bottom: 20px; }
      .company-name { font-size: 1.5em; font-weight: bold; color: #059669; margin-bottom: 10px; }
      .vessel-name { font-size: 1.8em; font-weight: bold; margin: 10px 0; }
      .date-range { color: #666; margin-bottom: 10px; }
      .section { margin: 30px 0; page-break-inside: avoid; }
      .section-title { background: #f0f9ff; padding: 10px; font-weight: bold; font-size: 1.2em; border-left: 4px solid #059669; }
      table { width: 100%; border-collapse: collapse; margin: 15px 0; }
      th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
      th { background: #f8f9fa; font-weight: bold; }
      .status-completed { color: #059669; font-weight: bold; }
      .status-open { color: #dc2626; font-weight: bold; }
      .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 0.9em; color: #666; }
      .no-data { color: #666; font-style: italic; padding: 20px; text-align: center; }
      @media print { body { margin: 20px; } .section { page-break-inside: avoid; } }
    </style>
  `;
}

/**
 * Generate CSV header for reports
 * @param {string} title - Report title
 * @param {string} vesselName - Vessel name
 * @param {string} dateRange - Date range
 * @returns {string} CSV header content
 */
export function generateCSVHeader(title, vesselName, dateRange) {
  return `${title} - ${vesselName}\nReport Period: ${dateRange}\nGenerated: ${new Date().toLocaleString()}\n\n`;
}

/**
 * Escape CSV field content
 * @param {string} field - Field content
 * @returns {string} Escaped field content
 */
export function escapeCSVField(field) {
  if (typeof field !== 'string') {
    field = String(field || '');
  }
  
  // If field contains comma, quote, or newline, wrap in quotes and escape quotes
  if (field.includes(',') || field.includes('"') || field.includes('\n')) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  
  return field;
}

/**
 * Convert array to CSV row
 * @param {Array} row - Array of field values
 * @returns {string} CSV row string
 */
export function arrayToCSVRow(row) {
  return row.map(escapeCSVField).join(',');
}

/**
 * Generate backup data object
 * @param {Object} appData - Application data
 * @returns {Object} Backup data object
 */
export function generateBackupData(appData) {
  return {
    ...appData,
    exportDate: new Date().toISOString(),
    version: "2.0"
  };
}

/**
 * Get file extension for format
 * @param {string} format - Export format
 * @returns {string} File extension
 */
export function getFileExtension(format) {
  const extensions = {
    'pdf': 'html', // We generate HTML for now
    'html': 'html',
    'csv': 'csv',
    'json': 'json'
  };
  
  return extensions[format] || 'html';
}

/**
 * Validate export options
 * @param {Object} exportOptions - Export configuration
 * @returns {Object} Validation result
 */
export function validateExportOptions(exportOptions) {
  const errors = [];
  
  if (!exportOptions.vessel || !exportOptions.vessel.trim()) {
    errors.push("Vessel name is required");
  }
  
  if (!exportOptions.dateFrom || !exportOptions.dateTo) {
    errors.push("Date range is required");
  }
  
  if (new Date(exportOptions.dateFrom) > new Date(exportOptions.dateTo)) {
    errors.push("Start date must be before end date");
  }
  
  const hasReportTypes = Object.values(exportOptions.reportTypes || {}).some(Boolean);
  if (!hasReportTypes) {
    errors.push("At least one report type must be selected");
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}